/**
 * Project: Gus's Auto Dealership
 * Author: Eihab Khudhair
 * Description: Contact page with form and information
 */

'use client';

import { useState } from 'react';
import { carsApi } from '@/lib/api';
import { motion } from 'framer-motion';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaClock, FaCar } from 'react-icons/fa';

export default function ContactPage() {

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');

    try {
      await carsApi.submitContact(formData);
      setStatus('success');
      setFormData({ name: '', email: '', phone: '', message: '' });
      setTimeout(() => setStatus('idle'), 5000);
    } catch (error) {
      console.error('Failed to submit contact form:', error);
      setStatus('error');
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white" suppressHydrationWarning>
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-900 to-gray-900 text-white py-16">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <h1 className="text-5xl font-bold mb-4">Contact Gus&apos;s Auto Dealership</h1>
            <p className="text-xl text-blue-200">Get in touch with Ohio&apos;s premier auto dealership</p>
          </motion.div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="max-w-7xl mx-auto"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
                <div className="flex items-center mb-8">
                  <div className="bg-blue-100 p-3 rounded-full mr-4">
                    <FaCar className="text-3xl text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900">Visit Our Showroom</h2>
                    <p className="text-gray-600">Experience excellence in automotive sales</p>
                  </div>
                </div>

                <div className="space-y-8">
                  <div className="flex items-start group hover:bg-blue-50 p-4 rounded-xl transition-all duration-300">
                    <div className="bg-blue-100 p-3 rounded-lg mr-4 group-hover:bg-blue-200 transition-colors">
                      <FaMapMarkerAlt className="text-2xl text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-1 text-gray-900">Address</h3>
                      <p className="text-gray-700">
                        1840 Atlas Street
                        <br />
                        Columbus, OH 43228
                      </p>
                      <p className="text-sm text-gray-500 mt-2">Located near Westbelt Business Park</p>
                    </div>
                  </div>

                  <div className="flex items-start group hover:bg-blue-50 p-4 rounded-xl transition-all duration-300">
                    <div className="bg-blue-100 p-3 rounded-lg mr-4 group-hover:bg-blue-200 transition-colors">
                      <FaPhone className="text-2xl text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-1 text-gray-900">Phone Number</h3>
                      <p className="text-2xl font-bold text-blue-700">(614) 772-0734</p>
                      <p className="text-sm text-gray-500 mt-2">Direct line to our sales team</p>
                    </div>
                  </div>

                  <div className="flex items-start group hover:bg-blue-50 p-4 rounded-xl transition-all duration-300">
                    <div className="bg-blue-100 p-3 rounded-lg mr-4 group-hover:bg-blue-200 transition-colors">
                      <FaEnvelope className="text-2xl text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-1 text-gray-900">Email Address</h3>
                      <a
                        href="mailto:gusauto99@gmail.com"
                        className="text-blue-600 hover:text-blue-800 font-medium text-lg"
                      >
                        gusauto99@gmail.com
                      </a>
                      <p className="text-sm text-gray-500 mt-2">Typically respond within 1 business day</p>
                    </div>
                  </div>

                  <div className="flex items-start group hover:bg-blue-50 p-4 rounded-xl transition-all duration-300">
                    <div className="bg-blue-100 p-3 rounded-lg mr-4 group-hover:bg-blue-200 transition-colors">
                      <FaClock className="text-2xl text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-1 text-gray-900">Business Hours</h3>

                      <div className="mt-4 space-y-3">
                        <div className="grid grid-cols-2 gap-8 pb-2 border-b border-gray-200">
                          <div className="text-gray-600 font-medium">Day</div>
                          <div className="text-gray-600 font-medium text-right">Hours</div>
                        </div>

                        <div className="grid grid-cols-2 gap-8">
                          <div className="text-gray-700">Monday - Friday</div>
                          <div className="font-semibold text-gray-900 text-right">9:00 AM - 6:00 PM</div>
                        </div>

                        <div className="grid grid-cols-2 gap-8">
                          <div className="text-gray-700">Saturday</div>
                          <div className="font-semibold text-gray-900 text-right">10:00 AM - 4:00 PM</div>
                        </div>

                        <div className="grid grid-cols-2 gap-8">
                          <div className="text-gray-700">Sunday</div>
                          <div className="font-semibold text-red-600 text-right">Closed</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Interactive Map */}
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="p-6 border-b">
                  <h3 className="text-xl font-bold text-gray-900">Find Us on the Map</h3>
                </div>
                <div className="h-96 relative">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3057.454040152924!2d-83.123456!3d39.987654!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMznCsDU5JzE1LjYiTiA4M8KwMDcnMjIuNyJX!5e0!3m2!1sen!2sus!4v1234567890"
                    width="100%"
                    height="100%"
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="absolute inset-0 border-0"
                    title="Gus's Auto Dealership Location"
                  />
                  <div className="absolute bottom-4 left-4 bg-white p-4 rounded-lg shadow-lg">
                    <div className="flex items-center">
                      <FaMapMarkerAlt className="text-red-500 mr-2" />
                      <div>
                        <p className="font-bold text-sm">Gus&apos;s Auto Dealership</p>
                        <p className="text-xs text-gray-600">1840 Atlas St, Columbus OH</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Send Us a Message</h2>
                <p className="text-gray-600">
                  Have questions about our vehicles? Looking for a specific model? Fill out the form below and our team will get back to you promptly.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6" autoComplete="on">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-900">Full Name *</label>
                    <input
                      type="text"
                      required
                      autoComplete="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full border border-gray-300 rounded-xl px-4 py-3 text-gray-900 placeholder-gray-400
                      focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white"
                      placeholder="John Smith"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-900">Email Address *</label>
                    <input
                      type="email"
                      required
                      autoComplete="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full border border-gray-300 rounded-xl px-4 py-3 text-gray-900 placeholder-gray-400
                      focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-900">Phone Number</label>
                  <input
                    type="tel"
                    autoComplete="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 text-gray-900 placeholder-gray-400
                    focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all bg-white"
                    placeholder="(614) 555-0123"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-900">Your Message *</label>
                  <textarea
                    required
                    rows={6}
                    spellCheck={false}
                    autoComplete="off"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 bg-white text-gray-900 caret-gray-900 placeholder-gray-400
                    focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all resize-none"
                    placeholder="Tell us about your vehicle needs, any specific models you're interested in, or questions about financing..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full bg-gradient-to-r from-blue-600 to-blue-800 text-white px-6 py-4 rounded-xl font-bold text-lg
                  hover:from-blue-700 hover:to-blue-900 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed
                  transform hover:-translate-y-1 shadow-lg hover:shadow-xl"
                >
                  {status === 'loading' ? (
                    <span className="flex items-center justify-center">
                      <svg
                        className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Sending Message...
                    </span>
                  ) : (
                    'Send Message'
                  )}
                </button>

                {status === 'success' && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-green-50 border border-green-200 text-green-800 p-4 rounded-xl"
                  >
                    <div className="flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <div>
                        <p className="font-bold">Message Sent Successfully!</p>
                        <p className="text-sm">
                          Thank you for contacting Gus&apos;s Auto Dealership. We&apos;ll respond within 24 hours.
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}

                {status === 'error' && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-xl"
                  >
                    <div className="flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <div>
                        <p className="font-bold">Unable to Send Message</p>
                        <p className="text-sm">Please try again or call us directly at (614) 772-0734</p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </form>

              <div className="mt-8 pt-8 border-t border-gray-200">
                <h3 className="font-bold text-gray-900 mb-4">What Happens Next?</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center p-5 bg-gradient-to-br from-blue-700 to-blue-800 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                    <div className="text-3xl font-bold mb-2">1</div>
                    <p className="text-sm font-semibold">We review your inquiry</p>
                  </div>
                  <div className="text-center p-5 bg-gradient-to-br from-blue-700 to-blue-800 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                    <div className="text-3xl font-bold mb-2">2</div>
                    <p className="text-sm font-semibold">Match you with perfect vehicles</p>
                  </div>
                  <div className="text-center p-5 bg-gradient-to-br from-blue-700 to-blue-800 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                    <div className="text-3xl font-bold mb-2">3</div>
                    <p className="text-sm font-semibold">Schedule a test drive</p>
                  </div>
                </div>
              </div>

              <div className="mt-10 text-center text-sm text-gray-500">
                © {new Date().getFullYear()} Gus’s Auto Dealership • Developed by Eihab Khudhair
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
